import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const SIM_DEFS = '/machinesim/api/sim-defs';

const FAMILIES = ['lathe', 'mill', 'swiss', 'press', 'grinder', 'robotic_cell'];

function apiErr(e) {
  const fromBody =
    e.response?.data &&
    typeof e.response.data === 'object' &&
    e.response.data.error != null
      ? String(e.response.data.error)
      : null;
  return fromBody || e.message || 'Request failed';
}

const emptyForm = () => ({
  code: '',
  name: '',
  family: 'lathe',
  category: '',
  description: '',
  defaultAssetKey: '',
  defaultTopicTemplate: '',
  enabled: true,
  capabilitiesJson: '',
});

export default function StreamMachineTypeForm() {
  const { machineTypeId } = useParams();
  const isEdit = Boolean(machineTypeId);
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(`${SIM_DEFS}/machine-types/${machineTypeId}`);
      const mt = res.data;
      let capabilitiesJson = '';
      if (mt.capabilities && typeof mt.capabilities === 'object') {
        try {
          capabilitiesJson = JSON.stringify(mt.capabilities, null, 2);
        } catch {
          capabilitiesJson = '';
        }
      }
      setForm({
        code: mt.code ?? '',
        name: mt.name ?? '',
        family: FAMILIES.includes(mt.family) ? mt.family : 'lathe',
        category: mt.category ?? '',
        description: mt.description ?? '',
        defaultAssetKey: mt.defaultAssetKey ?? '',
        defaultTopicTemplate: mt.defaultTopicTemplate ?? '',
        enabled: mt.enabled !== false,
        capabilitiesJson,
      });
    } catch (e) {
      setLoadError(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [isEdit, machineTypeId]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      let capabilities = null;
      const raw = form.capabilitiesJson.trim();
      if (raw) {
        capabilities = JSON.parse(raw);
        if (typeof capabilities !== 'object' || capabilities === null) {
          throw new Error('Capabilities must be a JSON object');
        }
      }

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        family: form.family,
        category: form.category.trim() || null,
        description: form.description.trim() || null,
        defaultAssetKey: form.defaultAssetKey.trim() || null,
        defaultTopicTemplate: form.defaultTopicTemplate.trim() || null,
        enabled: form.enabled,
        capabilities,
      };

      if (!payload.code || !payload.name) {
        throw new Error('Code and name are required');
      }

      if (isEdit) {
        const patch = { ...payload };
        delete patch.code;
        await axios.patch(`${SIM_DEFS}/machine-types/${machineTypeId}`, patch);
      } else {
        await axios.post(`${SIM_DEFS}/machine-types`, payload);
      }

      navigate('/stream/machine-types');
    } catch (e) {
      if (e instanceof SyntaxError) {
        setSaveError('Invalid JSON in capabilities');
      } else {
        setSaveError(apiErr(e));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="p-8 text-muted-foreground">Loading machine type…</div>
    );
  }

  if (loadError && isEdit) {
    return (
      <div className="p-4 md:p-8 max-w-xl mx-auto space-y-4">
        <p className="text-destructive">{loadError}</p>
        <Link to="/stream/machine-types" className="text-primary hover:underline text-sm">
          ← Back to definitions
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto pb-16">
      <div className="mb-6">
        <Link to="/stream/machine-types" className="text-sm text-muted-foreground hover:text-foreground">
          ← Machine type definitions
        </Link>
        <h2 className="text-xl font-semibold text-foreground mt-2">
          {isEdit ? 'Edit machine type' : 'New machine type'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Saved to the same database as stream fields and ERP metadata. Uses{' '}
          <span className="font-mono text-muted-foreground">POST/PATCH /sim-defs/machine-types</span>.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <label className="block">
          <span className="text-xs text-muted-foreground">code *</span>
          <input
            type="text"
            required
            disabled={isEdit}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-foreground disabled:opacity-60"
            value={form.code}
            onChange={(e) => setField('code', e.target.value)}
            placeholder="e.g. CNC_LATHE"
          />
          {isEdit && (
            <span className="text-xs text-muted-foreground mt-1 block">Code is fixed after creation.</span>
          )}
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">name *</span>
          <input
            type="text"
            required
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Display name"
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">family *</span>
          <select
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            value={form.family}
            onChange={(e) => setField('family', e.target.value)}
          >
            {FAMILIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">category</span>
          <input
            type="text"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            value={form.category}
            onChange={(e) => setField('category', e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">description</span>
          <textarea
            rows={3}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">defaultAssetKey</span>
          <input
            type="text"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm text-foreground"
            value={form.defaultAssetKey}
            onChange={(e) => setField('defaultAssetKey', e.target.value)}
          />
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">defaultTopicTemplate</span>
          <input
            type="text"
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm text-foreground"
            value={form.defaultTopicTemplate}
            onChange={(e) => setField('defaultTopicTemplate', e.target.value)}
          />
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setField('enabled', e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm text-foreground">enabled</span>
        </label>

        <label className="block">
          <span className="text-xs text-muted-foreground">capabilities (JSON object, optional)</span>
          <textarea
            rows={6}
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
            value={form.capabilitiesJson}
            onChange={(e) => setField('capabilitiesJson', e.target.value)}
            placeholder={'{\n  "supportsProgram": true\n}'}
          />
        </label>

        {saveError && (
          <p className="text-sm text-cn-warning" role="alert">
            {saveError}
          </p>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary hover:opacity-90 text-primary-foreground disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create machine type'}
          </button>
          <Link
            to="/stream/machine-types"
            className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-accent"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
